import type { FiatPrice } from "@b2bportal/air-disruption-api";
import { FlightShopSelectors } from "@b2bportal/core-flights";
import { createSelector } from "@reduxjs/toolkit";

import type { FlightState } from "../../slice";
import { AccountSelectors } from "@/modules/account";
import type { RootState } from "@/store";
import { getIsFixedPriceFromBasePrice } from "@/utilities/rewards/pricing";

export const getPricingMode = (state: FlightState) =>
  state.flights?.flightShop?.pricingMode;

/**
 * True when the shop fare is a fixed-points (grid/reward) fare, derived from the
 * actual fare structure (base paid in points) rather than the pricing tab.
 * Mirrors the fixed-price detection used by the marketplace price breakdown
 * (useFlightsTripPriceBreakdown), which renders on the same page.
 */
export const getIsShopFixedPriceFare = createSelector(
  [
    (state: RootState) => FlightShopSelectors.shopPricingInfoSelector(state),
    (state: RootState) => AccountSelectors.getRewardsAccountId(state),
  ],
  (shopPricingInfo, rewardsAccountId): boolean => {
    if (!rewardsAccountId) return false;
    const baseAmount = shopPricingInfo?.fare?.[0]?.pricing?.baseAmount;
    if (!baseAmount) return false;
    return Boolean(getIsFixedPriceFromBasePrice(baseAmount, rewardsAccountId));
  }
);

/** RBC default currency when API omits it */
const DEFAULT_CURRENCY: Pick<FiatPrice, "currencyCode" | "currencySymbol"> = {
  currencyCode: "CAD",
  currencySymbol: "C$",
};

/**
 * For grid/reward (fixed price) bookings: what the user sees as the flight cost, converted to money.
 *
 * Calculates points * basePointsToCashMultiplier + tax from shop pricing info.
 * (displayedTicketPrice will be computed in BFF when that logic is moved.)
 *
 * Returns undefined when:
 * - Not a fixed-price fare or no trip selected
 * - No valid conversion data (shopPricingInfo, conversion rate, rewards account)
 */
export const getDisplayedTicketPriceForDisruption = createSelector(
  [
    getPricingMode,
    (state: RootState) => FlightShopSelectors.shopPricingInfoSelector(state),
    (state: RootState) => AccountSelectors.getBasePointsToCashMultiplier(state),
    (state: RootState) => AccountSelectors.getRewardsAccountId(state),
  ],
  (
    pricingMode,
    shopPricingInfo,
    basePointsToCashMultiplier,
    rewardsAccountId
  ): FiatPrice | undefined => {
    if (pricingMode !== "fixed") return undefined;

    // Convert points + tax from shop pricing info
    if (!shopPricingInfo?.fare?.length) return undefined;
    const paxPricing = shopPricingInfo.fare[0]?.pricing;
    if (!paxPricing) return undefined;
    const totalFiat = paxPricing.total?.fiat?.value ?? 0;
    if (totalFiat > 0) return undefined; // Cash fare, not grid
    const baseAmount = paxPricing.baseAmount;
    const taxAmount = paxPricing.taxAmount;
    if (!taxAmount?.fiat) return undefined;

    // Require rewards account to avoid inflating price by summing across all accounts
    if (
      !rewardsAccountId ||
      baseAmount?.accountSpecific?.[rewardsAccountId]?.value == null
    ) {
      return undefined;
    }
    const points = baseAmount.accountSpecific[rewardsAccountId].value;

    // Require conversion rate; do not send partial (taxes-only) price to disruption API
    if (!basePointsToCashMultiplier || basePointsToCashMultiplier <= 0) {
      return undefined;
    }

    // FiatPrice.value is number; taxAmount.fiat validated above
    const taxValue = taxAmount.fiat.value;
    // 1e9/1e7 = 1e2: the cents conversion is folded into the denominator.
    // Separating them (x * 100 * 1e9) would overflow MAX_SAFE_INTEGER above ~9M
    // points — unlikely in practice, but no reason to leave the footgun.
    const pointsCashEquivalentCents = Math.floor(
      Math.round(points * basePointsToCashMultiplier * 1e9) / 1e7
    );

    // Avoid sending NaN or invalid values to the API
    if (
      !Number.isFinite(pointsCashEquivalentCents) ||
      pointsCashEquivalentCents < 0 ||
      !Number.isFinite(taxValue) ||
      taxValue < 0
    ) {
      return undefined;
    }

    const totalValue =
      (pointsCashEquivalentCents + Math.round(taxValue * 100)) / 100;

    return {
      currencyCode:
        taxAmount.fiat.currencyCode ?? DEFAULT_CURRENCY.currencyCode,
      currencySymbol:
        taxAmount.fiat.currencySymbol ?? DEFAULT_CURRENCY.currencySymbol,
      value: totalValue,
    };
  }
);

/**
 * Coarse signal: the fixed-points shop summary failed for any reason (insufficient
 * points, route rules, network, etc.). Use for tabs / flex fallback UX.
 */
export const getIsSearchedRouteUnavailableForGridPurchase = (
  state: FlightState
) => state.flights.flightShop.routeNotAllowedForFixedPointsPricing;

/** @deprecated Prefer {@link getIsSearchedRouteUnavailableForGridPurchase} */
export const getRouteNotAllowedForFixedPointsPricing =
  getIsSearchedRouteUnavailableForGridPurchase;

/**
 * Refined: last fixed-points V4 shop summary failed with
 * `RouteNotAllowedForFixedPointsPricing` (destination/route ineligible for grid per RBC rules).
 */
export const getIsShopSummaryDestinationIneligibleForGridPricing = (
  state: FlightState
) => state.flights.flightShop.shopSummaryDestinationIneligibleForGridPricing;

/**
 * Refined: last fixed-points V4 shop summary failed with
 * `InsufficientPointsBalanceForFixedPointsPricing` (user has insufficient points).
 */
export const getIsShopSummaryInsufficientPointsBalance = (state: FlightState) =>
  state.flights.flightShop.shopSummaryInsufficientPointsBalance;

export const getAdditionalPointsPurchase = (state: FlightState) =>
  state.flights.flightShop.additionalPointsPurchase;
